/**
 * Co-tasting (docs/design/social-plan.md 4.4, founder decisions of 2026-10-23) -- pure rules, no
 * I/O. What two people in one shared tasting may see of each other while they taste, and how the
 * group's verdicts are summed up afterwards. The blind rule lives here once so the sender, the
 * receiver and the tests cannot disagree: a blind tasting shows progress, never answers.
 */
import { QUALITY, type Quality } from "../scales.js";

/** The pages of the guided pager, in order; a quick tasting is one page and has none. */
export const LIVE_STEPS = ["look", "smell", "taste", "structure", "summary"] as const;
export type LiveStep = (typeof LIVE_STEPS)[number];

/** At most this many aroma families per phase travel in one event, which keeps it under the mesh's size limit. */
export const LIVE_FAMILY_CAP = 6;

/** A verdict on the five-point scale; a faulty bottle is a fault, not a place on it. */
export const LIVE_VERDICTS = QUALITY.filter((quality) => quality !== "faulty");

/** What a person may say to the group about a wine, in characters. */
export const GROUP_QUOTE_MAX = 140;

/** Characters as a person counts them: an emoji is one, though it takes two UTF-16 units. */
export function quoteLength(text: string): number {
  return Array.from(text).length;
}

export interface LivePicksInput {
  /** Blind tastings never carry answers or descriptors before the reveal. */
  readonly blind: boolean;
  /** Whether other people are in the tasting at all (a session); a solo one shows nothing. */
  readonly shared: boolean;
  /** This taster's own switch ("share my picks live"). */
  readonly optedIn: boolean;
}

/**
 * Whether this device sends its picks and whether it takes other people's. Opting out only stops
 * what this person sends: seeing the others' picks is not a thing they have to give up.
 */
export function livePicksFlow(input: LivePicksInput): { send: boolean; accept: boolean } {
  const open = input.shared && !input.blind;
  return { send: open && input.optedIn, accept: open };
}

/**
 * A line for the group, made safe to store and show as plain text: control characters become
 * spaces, runs of whitespace one space, and it is cut at {@link GROUP_QUOTE_MAX} characters. Markup
 * is left alone (it is data); nothing renders it as markup. Blank text is no quote.
 */
export function normaliseQuote(text: string): string | undefined {
  // oxlint-disable-next-line eslint/no-control-regex -- stripping control characters is the point.
  const folded = text.normalize("NFC").replace(/[\u0000-\u001f\u007f-\u009f]/g, " ");
  const clean = folded.replace(/\s+/g, " ").trim();
  if (clean === "") return undefined;
  return Array.from(clean).slice(0, GROUP_QUOTE_MAX).join("").trimEnd();
}

export interface VerdictSummary {
  /** Verdicts counted, a faulty one not among them. */
  readonly count: number;
  /** The middle verdict; of an even count the lower of the two, so the group is never flattered. */
  readonly median?: Quality;
  /** The verdict most people gave; none when two or more lead together. */
  readonly mode?: Quality;
}

export function verdictSummary(verdicts: readonly Quality[]): VerdictSummary {
  const rank = (quality: Quality): number => LIVE_VERDICTS.findIndex((v) => v === quality);
  const scored = verdicts
    .filter((quality) => quality !== "faulty")
    .toSorted((a, b) => rank(a) - rank(b));
  const median = scored[Math.floor((scored.length - 1) / 2)];
  if (median === undefined) return { count: 0 };
  const tally = new Map<Quality, number>();
  for (const quality of scored) tally.set(quality, (tally.get(quality) ?? 0) + 1);
  const top = Math.max(...tally.values());
  const leaders = [...tally].filter(([, n]) => n === top);
  const mode = leaders.length === 1 ? leaders[0]?.[0] : undefined;
  return { count: scored.length, median, ...(mode === undefined ? {} : { mode }) };
}
